import { useEffect, useState, useMemo, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Search, Mail, MousePointerClick, Pencil, MessageSquare } from "lucide-react";
import AdminBroadcastEmailTab from "@/components/AdminBroadcastEmailTab";
import AdminEditUserDialog from "@/components/AdminEditUserDialog";
import AdminMediationsTab from "@/components/AdminMediationsTab";
import AdminModerationTab from "@/components/AdminModerationTab";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function AdminPanel() {
  const [tab, setTab] = useState("businesses");
  const [users, setUsers] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set()); // ids selected in current tab
  const [promoCooldown, setPromoCooldown] = useState(7);
  const [promoCooldownDraft, setPromoCooldownDraft] = useState("7");
  const [savingCooldown, setSavingCooldown] = useState(false);
  const [welcomeReport, setWelcomeReport] = useState({ items: [], totals: null });
  const [webhookHealth, setWebhookHealth] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  // When set, the Reviews tab is narrowed to reviews on this profile.
  // Populated by the "View reviews" button on Businesses / Customers rows.
  const [reviewTargetFilter, setReviewTargetFilter] = useState(null); // {id, name, role}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, r, s, w, h] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/reviews"),
        api.get("/admin/settings/promotion-cooldown"),
        api.get("/admin/welcome-email-report"),
        api.get("/admin/webhook-health"),
      ]);
      setUsers(u.data);
      setReviews(r.data);
      const days = s.data?.days ?? 7;
      setPromoCooldown(days);
      setPromoCooldownDraft(String(days));
      setWelcomeReport(w.data || { items: [], totals: null });
      setWebhookHealth(h.data || null);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveCooldown = async () => {
    const n = Number(promoCooldownDraft);
    if (!Number.isFinite(n) || n < 0 || n > 365 || !Number.isInteger(n)) {
      toast.error("Enter a whole number between 0 and 365 (0 disables the limit).");
      return;
    }
    setSavingCooldown(true);
    try {
      const { data } = await api.put("/admin/settings/promotion-cooldown", { days: n });
      setPromoCooldown(data.days);
      toast.success(n === 0 ? "Promotion cooldown disabled" : `Promotion cooldown set to ${data.days} day${data.days === 1 ? "" : "s"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSavingCooldown(false);
    }
  };

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setSelected(new Set()); }, [tab, q]);

  const ql = q.toLowerCase();
  const matchesUser = (u) => !ql || (u.email || "").toLowerCase().includes(ql) ||
    (u.business_name || "").toLowerCase().includes(ql) ||
    (u.name || "").toLowerCase().includes(ql);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const businesses = useMemo(() => users.filter(u => u.role === "business" && matchesUser(u)), [users, ql]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const customers = useMemo(() => users.filter(u => u.role === "customer" && matchesUser(u)), [users, ql]);
  const visibleReviews = useMemo(() => {
    let base = reviews;
    if (reviewTargetFilter?.id) {
      base = base.filter((r) => r.target_id === reviewTargetFilter.id);
    }
    if (!ql) return base;
    return base.filter((r) =>
      (r.comment || "").toLowerCase().includes(ql) ||
      (r.author_name || "").toLowerCase().includes(ql) ||
      (r.target_name || "").toLowerCase().includes(ql) ||
      (r.target_email || "").toLowerCase().includes(ql));
  }, [reviews, ql, reviewTargetFilter]);

  // Count reviews per profile so the "View reviews" button on Businesses /
  // Customers rows can show a live badge with the number of reviews on
  // that profile without an extra API call.
  const reviewsByTarget = useMemo(() => {
    const m = new Map();
    for (const r of reviews) {
      if (!r.target_id) continue;
      m.set(r.target_id, (m.get(r.target_id) || 0) + 1);
    }
    return m;
  }, [reviews]);

  // Jump into the Reviews tab pre-filtered to a specific profile.
  const viewReviewsFor = (u) => {
    const name = u.business_name || u.name || u.email;
    setReviewTargetFilter({ id: u.id, name, role: u.role });
    setQ("");
    setTab("reviews");
  };

  const currentList = tab === "businesses" ? businesses : tab === "customers" ? customers : visibleReviews;

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      if (s.size === currentList.length) return new Set();
      return new Set(currentList.map((x) => x.id));
    });
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const url = tab === "reviews" ? "/admin/reviews/bulk-delete" : "/admin/users/bulk-delete";
    try {
      const { data } = await api.post(url, { ids });
      toast.success(`Deleted ${data.deleted} ${tab === "reviews" ? "review(s)" : "user(s)"}`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteSingle = async (id) => {
    try {
      if (tab === "reviews") await api.delete(`/admin/reviews/${id}`);
      else await api.delete(`/admin/users/${id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const allSelected = currentList.length > 0 && selected.size === currentList.length;

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      <div className="overline text-gray-500">Admin</div>
      <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-2">Site moderation</h1>
      <p className="text-gray-600 mt-2">Select multiple rows to delete them in bulk. Deletion is permanent and cascades to reviews and notifications tied to deleted users.</p>

      <Card className="mt-6 rounded-sm border border-border p-5" data-testid="admin-settings-card">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <div className="overline text-gray-500">Site settings</div>
            <h3 className="font-display text-xl mt-1">Promotion email cooldown</h3>
            <p className="text-xs text-gray-500 mt-1 max-w-xl">
              How many days each business must wait between promotional message blasts. Current: <strong>{promoCooldown === 0 ? "no limit" : `${promoCooldown} day${promoCooldown === 1 ? "" : "s"}`}</strong>. Set <code className="font-mono">0</code> to disable the limit entirely.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={0}
              max={365}
              step={1}
              value={promoCooldownDraft}
              onChange={(e) => setPromoCooldownDraft(e.target.value)}
              className="rounded-sm w-24"
              data-testid="admin-promo-cooldown-input"
            />
            <span className="text-sm text-gray-500">days</span>
            <Button
              onClick={saveCooldown}
              disabled={savingCooldown || String(promoCooldown) === promoCooldownDraft}
              className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]"
              data-testid="admin-promo-cooldown-save"
            >
              {savingCooldown ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by name, email, or review text" value={q} onChange={(e) => setQ(e.target.value)} className="rounded-sm pl-9" data-testid="admin-search-input" />
        </div>
        {selected.size > 0 && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="admin-bulk-delete-button">
                <Trash2 size={14} className="mr-2" /> Delete selected ({selected.size})
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-sm">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete {selected.size} {tab === "reviews" ? "review(s)" : "account(s)"}?</AlertDialogTitle>
                <AlertDialogDescription>This is permanent. {tab !== "reviews" && "Their reviews and notifications will also be removed."}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={bulkDelete} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="admin-bulk-delete-confirm">Delete all</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="rounded-sm">
          <TabsTrigger value="businesses" className="rounded-sm" data-testid="admin-tab-businesses">Businesses ({businesses.length})</TabsTrigger>
          <TabsTrigger value="customers" className="rounded-sm" data-testid="admin-tab-customers">Customers ({customers.length})</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-sm" data-testid="admin-tab-reviews">Reviews ({visibleReviews.length})</TabsTrigger>
          <TabsTrigger value="mediations" className="rounded-sm" data-testid="admin-tab-mediations">Mediations</TabsTrigger>
          <TabsTrigger value="moderation" className="rounded-sm" data-testid="admin-tab-moderation">Moderation queue</TabsTrigger>
          <TabsTrigger value="welcome-emails" className="rounded-sm" data-testid="admin-tab-welcome-emails">
            Welcome emails ({welcomeReport.items?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="broadcast" className="rounded-sm" data-testid="admin-tab-broadcast">
            Broadcast email
          </TabsTrigger>
        </TabsList>

        {currentList.length > 0 && (
          <div className="mt-6 flex items-center gap-2 px-2">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="admin-select-all" />
            <span className="text-xs text-gray-500">Select all on this tab</span>
          </div>
        )}

        <TabsContent value="businesses">
          <div className="mt-3 space-y-2" data-testid="admin-list-businesses">
            {loading && <p className="text-gray-500">Loading...</p>}
            {!loading && businesses.length === 0 && <p className="text-gray-500 text-sm">No businesses match.</p>}
            {businesses.map((u) => (
              <Card key={u.id} className="rounded-sm border border-border p-4 flex items-center gap-3" data-testid={`admin-business-${u.id}`}>
                <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`admin-select-${u.id}`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium">{u.business_name || "(no name)"}</p>
                    {!u.claimed && <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-amber-400 text-amber-700">Unclaimed</Badge>}
                    {u.subscription_status === "active" && <Badge className="rounded-sm text-[10px] font-mono bg-emerald-500 hover:bg-emerald-500">Active sub</Badge>}
                  </div>
                  <p className="text-xs font-mono text-gray-500">{u.email}</p>
                  <p className="text-xs text-gray-500">{u.business_address}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => viewReviewsFor(u)}
                  className="rounded-[35px] shadow-sm hover:shadow-md text-[#B31E1E] hover:bg-[#B31E1E]/5 relative"
                  title="View reviews on this profile"
                  data-testid={`admin-view-reviews-${u.id}`}
                >
                  <MessageSquare size={16} />
                  {reviewsByTarget.get(u.id) > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#B31E1E] text-white text-[9px] font-mono font-bold flex items-center justify-center">
                      {reviewsByTarget.get(u.id)}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingUser(u)}
                  className="rounded-[35px] shadow-sm hover:shadow-md text-[#B31E1E] hover:bg-[#B31E1E]/5"
                  title="Edit profile"
                  data-testid={`admin-edit-${u.id}`}
                >
                  <Pencil size={16} />
                </Button>
                <SingleDelete onConfirm={() => deleteSingle(u.id)} testId={`admin-delete-${u.id}`} label="business" />
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="customers">
          <div className="mt-3 space-y-2" data-testid="admin-list-customers">
            {!loading && customers.length === 0 && <p className="text-gray-500 text-sm">No customers match.</p>}
            {customers.map((u) => (
              <Card key={u.id} className="rounded-sm border border-border p-4 flex items-center gap-3" data-testid={`admin-customer-${u.id}`}>
                <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`admin-select-${u.id}`} />
                <div className="flex-1">
                  <p className="font-medium">{u.name}</p>
                  <p className="text-xs font-mono text-gray-500">{u.email}</p>
                  <p className="text-xs text-gray-500">{[u.street_full || u.street, u.city, u.state, u.zip].filter(Boolean).join(", ")}</p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => viewReviewsFor(u)}
                  className="rounded-[35px] shadow-sm hover:shadow-md text-[#B31E1E] hover:bg-[#B31E1E]/5 relative"
                  title="View reviews on this profile"
                  data-testid={`admin-view-reviews-${u.id}`}
                >
                  <MessageSquare size={16} />
                  {reviewsByTarget.get(u.id) > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-[#B31E1E] text-white text-[9px] font-mono font-bold flex items-center justify-center">
                      {reviewsByTarget.get(u.id)}
                    </span>
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setEditingUser(u)}
                  className="rounded-[35px] shadow-sm hover:shadow-md text-[#B31E1E] hover:bg-[#B31E1E]/5"
                  title="Edit profile"
                  data-testid={`admin-edit-${u.id}`}
                >
                  <Pencil size={16} />
                </Button>
                <SingleDelete onConfirm={() => deleteSingle(u.id)} testId={`admin-delete-${u.id}`} label="customer" />
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="reviews">
          {reviewTargetFilter && (
            <div
              className="mt-3 rounded-sm border border-amber-300 bg-amber-50 px-3 py-2 flex items-center justify-between"
              data-testid="admin-review-target-filter-banner"
            >
              <p className="text-xs text-amber-900">
                Showing reviews on{" "}
                <strong>{reviewTargetFilter.name}</strong>
                {reviewTargetFilter.role ? ` (${reviewTargetFilter.role})` : ""}
              </p>
              <Button
                size="sm"
                variant="ghost"
                className="rounded-sm text-amber-900 hover:bg-amber-100 h-7 text-xs"
                onClick={() => setReviewTargetFilter(null)}
                data-testid="admin-review-target-filter-clear"
              >
                Clear filter
              </Button>
            </div>
          )}
          <div className="mt-3 space-y-2">
            {!loading && visibleReviews.length === 0 && (
              <p className="text-gray-500 text-sm">
                {reviewTargetFilter
                  ? "No reviews on this profile yet."
                  : "No reviews."}
              </p>
            )}
            {visibleReviews.map((r) => (
              <Card key={r.id} className="rounded-sm border border-border p-4 flex items-center gap-3" data-testid={`admin-review-${r.id}`}>
                <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggle(r.id)} data-testid={`admin-select-${r.id}`} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-mono text-gray-500">
                    {r.type} · {r.rating}★ · {new Date(r.created_at).toLocaleDateString()}
                    {r.target_name && (
                      <>
                        {" · on "}
                        <button
                          type="button"
                          className="text-[#B31E1E] hover:underline"
                          onClick={() => setReviewTargetFilter({ id: r.target_id, name: r.target_name, role: r.target_role })}
                          data-testid={`admin-review-target-${r.id}`}
                        >
                          {r.target_name}
                        </button>
                        {r.target_role ? ` (${r.target_role})` : ""}
                      </>
                    )}
                  </p>
                  <p className="text-sm mt-1"><strong>{r.author_name}</strong>: {r.comment}</p>
                </div>
                <SingleDelete onConfirm={() => deleteSingle(r.id)} testId={`admin-delete-review-${r.id}`} label="review" />
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="welcome-emails">
          <WebhookHealthWidget health={webhookHealth} loading={loading} />
          <WelcomeEmailReport report={welcomeReport} loading={loading} />
        </TabsContent>

        <TabsContent value="mediations">
          <AdminMediationsTab />
        </TabsContent>

        <TabsContent value="moderation">
          <AdminModerationTab />
        </TabsContent>

        <TabsContent value="broadcast">
          <AdminBroadcastEmailTab users={users} loading={loading} />
        </TabsContent>
      </Tabs>

      <AdminEditUserDialog
        user={editingUser}
        open={!!editingUser}
        onOpenChange={(o) => { if (!o) setEditingUser(null); }}
        onSaved={load}
      />
    </div>
  );
}

function SingleDelete({ onConfirm, testId, label }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="ghost" size="icon" className="rounded-[35px] shadow-sm hover:shadow-md text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={testId}>
          <Trash2 size={16} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="rounded-sm">
        <AlertDialogHeader>
          <AlertDialogTitle>Delete this {label}?</AlertDialogTitle>
          <AlertDialogDescription>This is permanent. Reviews and notifications tied to it will also be removed.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} className="rounded-sm bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}


function WelcomeEmailReport({ report, loading }) {
  const items = report?.items || [];
  const totals = report?.totals;

  const formatTs = (ts) => {
    if (!ts) return "—";
    try {
      const d = new Date(ts);
      return d.toLocaleString();
    } catch {
      return ts;
    }
  };

  return (
    <div className="mt-3 space-y-5" data-testid="admin-welcome-report">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="rounded-sm border border-border p-4">
          <div className="flex items-center gap-2 text-gray-500">
            <Mail size={14} />
            <span className="text-[11px] font-mono uppercase tracking-wider">Listings emailed</span>
          </div>
          <p className="font-display text-3xl mt-2 tabular-nums" data-testid="welcome-totals-emailed">
            {totals?.listings_emailed ?? 0}
          </p>
        </Card>
        <Card className="rounded-sm border border-border p-4">
          <div className="flex items-center gap-2 text-gray-500">
            <MousePointerClick size={14} />
            <span className="text-[11px] font-mono uppercase tracking-wider">Listings clicked</span>
          </div>
          <p className="font-display text-3xl mt-2 tabular-nums" data-testid="welcome-totals-clicked">
            {totals?.listings_clicked ?? 0}
          </p>
        </Card>
        <Card className="rounded-sm border border-border p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Click-through rate</div>
          <p className="font-display text-3xl mt-2 tabular-nums text-[#B31E1E]" data-testid="welcome-totals-ctr">
            {totals?.click_through_rate_pct ?? 0}%
          </p>
        </Card>
        <Card className="rounded-sm border border-border p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Claimed after click</div>
          <p className="font-display text-3xl mt-2 tabular-nums text-emerald-600" data-testid="welcome-totals-claimed">
            {totals?.listings_claimed_after_click ?? 0}
          </p>
        </Card>
        <Card className="rounded-sm border border-border p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-gray-500">Total click events</div>
          <p className="font-display text-3xl mt-2 tabular-nums">
            {totals?.click_events ?? 0}
          </p>
        </Card>
      </div>

      {loading && items.length === 0 ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : items.length === 0 ? (
        <Card className="rounded-sm border border-dashed border-border p-6 text-center" data-testid="welcome-report-empty">
          <p className="text-sm text-gray-600">
            No welcome emails have been sent yet. Once businesses are added via the public submission form they&apos;ll appear here.
          </p>
        </Card>
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr className="text-left">
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Business</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Email</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Sent</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider text-right">Clicks</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Last click</th>
                <th className="px-3 py-2 text-[11px] font-mono uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {items.map((r) => (
                <tr key={r.business_id} data-testid={`welcome-row-${r.business_id}`} className="hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium text-gray-900 max-w-[220px] truncate" title={r.business_name}>
                    {r.business_name || "(no name)"}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600 max-w-[220px] truncate" title={r.email}>
                    {r.email || "—"}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-right">{r.sent_count}</td>
                  <td className="px-3 py-2 tabular-nums text-right">
                    {r.click_count > 0 ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 text-xs font-semibold">
                        <MousePointerClick size={10} /> {r.click_count}
                      </span>
                    ) : (
                      <span className="text-gray-400">0</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{formatTs(r.last_click_at)}</td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {r.claimed ? (
                        <Badge className="rounded-sm text-[10px] font-mono bg-emerald-500 hover:bg-emerald-500">Claimed</Badge>
                      ) : (
                        <Badge variant="outline" className="rounded-sm text-[10px] font-mono border-amber-400 text-amber-700">Unclaimed</Badge>
                      )}
                      {r.subscription_active && (
                        <Badge className="rounded-sm text-[10px] font-mono bg-[#B31E1E] hover:bg-[#B31E1E]">Pro</Badge>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}




function WebhookHealthWidget({ health, loading }) {
  if (loading && !health) {
    return (
      <Card className="rounded-sm border border-border p-4 mb-5">
        <p className="text-sm text-gray-500">Loading webhook health…</p>
      </Card>
    );
  }
  if (!health) return null;

  const TYPE_LABEL = {
    sent: "Sent",
    delivered: "Delivered",
    opened: "Opened",
    clicked: "Clicked",
    bounced: "Bounced",
    complained: "Complained",
    delivery_delayed: "Delayed",
  };
  const TYPE_COLOR = {
    sent: "text-gray-700 bg-gray-100 border-gray-300",
    delivered: "text-emerald-700 bg-emerald-50 border-emerald-300",
    opened: "text-[#B31E1E] bg-[#B31E1E]/5 border-[#B31E1E]/30",
    clicked: "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-300",
    bounced: "text-rose-700 bg-rose-50 border-rose-300",
    complained: "text-rose-700 bg-rose-50 border-rose-300",
    delivery_delayed: "text-amber-700 bg-amber-50 border-amber-300",
  };

  const formatTs = (ts) => {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  const lastSeenLabel = health.last_event_at
    ? formatTs(health.last_event_at)
    : "No events received yet";

  const secretOK = !!health.secret_configured;
  const hasTraffic = (health.total_events || 0) > 0;

  return (
    <Card className="rounded-sm border border-border p-5 mb-5 bg-white" data-testid="webhook-health-card">
      <div className="flex items-center gap-2 flex-wrap">
        <Mail size={16} className="text-[#B31E1E]" />
        <h3 className="font-display text-xl">Resend webhook health</h3>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
            secretOK
              ? "text-emerald-700 bg-emerald-50 border-emerald-300"
              : "text-amber-700 bg-amber-50 border-amber-300"
          }`}
          data-testid="webhook-secret-status"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${secretOK ? "bg-emerald-500" : "bg-amber-500"}`} />
          {secretOK ? "Secret configured" : "Secret missing"}
        </span>
        <span
          className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${
            hasTraffic
              ? "text-emerald-700 bg-emerald-50 border-emerald-300"
              : "text-gray-600 bg-gray-50 border-gray-300"
          }`}
          data-testid="webhook-traffic-status"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${hasTraffic ? "bg-emerald-500" : "bg-gray-400"}`} />
          {hasTraffic ? `${health.total_events} events received` : "Awaiting first event"}
        </span>
      </div>

      <p className="mt-2 text-xs font-mono text-gray-500" data-testid="webhook-last-event">
        Last event: {lastSeenLabel}
      </p>

      {!secretOK && (
        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-3 py-2">
          Set <code className="font-mono">RESEND_WEBHOOK_SECRET</code> in your Emergent Production Secrets, then redeploy. Until it&apos;s set the endpoint accepts every payload (scaffold mode) and signatures aren&apos;t verified.
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="webhook-type-grid">
        {Object.entries(health.by_type || {}).map(([t, v]) => (
          <div
            key={t}
            className={`rounded-sm border px-3 py-2 ${TYPE_COLOR[t] || "border-border bg-white text-gray-700"}`}
            data-testid={`webhook-type-${t}`}
          >
            <p className="text-[10px] font-mono uppercase tracking-wider opacity-80">{TYPE_LABEL[t] || t}</p>
            <p className="font-display text-2xl tabular-nums mt-0.5">{v.count}</p>
            <p className="text-[10px] font-mono opacity-60">
              {v.last_at ? formatTs(v.last_at) : "—"}
            </p>
          </div>
        ))}
      </div>

      {(health.recent || []).length > 0 && (
        <details className="mt-4 group">
          <summary className="text-xs font-mono text-[#B31E1E] cursor-pointer hover:underline" data-testid="webhook-recent-toggle">
            Show last {health.recent.length} raw events ↓
          </summary>
          <div className="mt-2 border border-border rounded-sm overflow-x-auto bg-gray-50/50">
            <table className="w-full text-xs">
              <thead className="text-gray-500">
                <tr className="text-left">
                  <th className="px-3 py-2 font-mono uppercase tracking-wider">When</th>
                  <th className="px-3 py-2 font-mono uppercase tracking-wider">Type</th>
                  <th className="px-3 py-2 font-mono uppercase tracking-wider">Promo</th>
                  <th className="px-3 py-2 font-mono uppercase tracking-wider">To</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {health.recent.map((e) => (
                  <tr key={e.id} data-testid={`webhook-recent-row-${e.id}`}>
                    <td className="px-3 py-1.5 font-mono text-gray-600 whitespace-nowrap">{formatTs(e.created_at)}</td>
                    <td className="px-3 py-1.5">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-mono ${
                        TYPE_COLOR[e.type] || "border-border bg-white text-gray-700"
                      }`}>
                        {e.type || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 font-mono text-gray-500 truncate max-w-[120px]" title={e.promo_id}>
                      {e.promo_id ? e.promo_id.slice(0, 8) : "—"}
                    </td>
                    <td className="px-3 py-1.5 font-mono text-gray-500 truncate max-w-[220px]" title={e.to || ""}>
                      {e.to || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </Card>
  );
}
